import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import {
  ProgramId,
  ProgramSectionResponse,
  ProgramSectionSortingResponse,
  ProgramTargetResponse,
  SectionId,
  TargetId,
} from '@business/entities/programs';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap, tap } from 'rxjs/operators';
import { ProgramSectionAPI } from '@business/api/program_section';
import { ProgramTargetsAPI } from '@business/api/program_targets';
import { UserId } from '@business/entities/account';

export class ProgramSectionBloc extends BaseBloc {
  private readonly programSectionRepo = new Repository<ProgramSectionResponse>({
    getItemId: item => item.id,
  });

  readonly programSections$ = this.programSectionRepo.items$;
  readonly selectSection = this.programSectionRepo.selectItem;

  onReset() {
    this.programSectionRepo.reset();
  }

  fetchSections = (id: ProgramId) => {
    return ProgramSectionAPI.fetchSections(id).pipe(
      this.programSectionRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_program_sections_error') }),
    );
  };

  createProgramSection = (data: Partial<ProgramSectionResponse>) => {
    return ProgramSectionAPI.createProgramSection(data).pipe(
      this.programSectionRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_program_sections'),
      }),
    );
  };

  selectOneSection = (sectionId: SectionId) => {
    return ProgramSectionAPI.selectOneSection(sectionId).pipe(
      this.programSectionRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_program_sections_error') }),
    );
  };

  deleteProgramSection = (id: SectionId) => {
    return showDeleteConfirmation('Delete Program Section', 'Do you really want to remove this Section?').pipe(
      switchMap(() => ProgramSectionAPI.deleteProgramSection(id)),
      this.programSectionRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_program_section'),
      }),
    );
  };

  updateProgramSection = (id: SectionId, data: Partial<ProgramSectionResponse>) => {
    return ProgramSectionAPI.updateProgramSection(id, data).pipe(
      this.programSectionRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_program_section'),
      }),
    );
  };

  sortProgramSection = (section: ProgramSectionResponse, fromPosition: any, toPosition: number, programId: ProgramId) => {
    return ProgramSectionAPI.sortProgramSection(section, fromPosition, toPosition, programId).pipe(
      this.programSectionRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        type: requestMessage('update_program_section'),
      }),
    );
  };

  sortTargets = (programId: ProgramId, section: any[]) => {
    return ProgramSectionAPI.sortTargets(programId, section).pipe(
      this.programSectionRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        type: requestMessage('update_program_section'),
      }),
    );
  };

  deleteProgramTarget = (id: TargetId, sectionId: SectionId) => {
    return showDeleteConfirmation('Delete Program Target', 'Do you really want to remove this Target?').pipe(
      switchMap(() => ProgramTargetsAPI.deleteProgramTarget(id)),
      tap(() => {
        const section = this.programSectionRepo.getItem(sectionId)!;
        const steps = section.steps.filter(step => step.id !== id);
        this.programSectionRepo.updateOne({
          id: sectionId,
          changes: () => ({ steps }),
        });
      }),
      handleMessage({
        type: requestMessage('delete_program_target'),
      }),
    );
  };

  createProgramTarget = (data: Partial<ProgramTargetResponse>) => {
    return ProgramTargetsAPI.createProgramTarget(data).pipe(
      tap(data => {
        const section = this.programSectionRepo.getItem(data.sectionId)!;
        const steps = section.steps.concat(data);
        this.programSectionRepo.updateOne({
          id: data.sectionId,
          changes: () => ({ steps }),
        });
      }),
      handleMessage({
        type: requestMessage('create_program_target'),
      }),
    );
  };

  updateProgramTarget = (id: TargetId, data: Partial<ProgramTargetResponse>) => {
    return ProgramTargetsAPI.updateProgramTarget(id, data).pipe(
      tap(data => {
        const section = this.programSectionRepo.getItem(data.sectionId)!;
        const steps = section.steps.map(step => {
          if (step.id === id) {
            return { ...step, ...data };
          }
          return step;
        });
        this.programSectionRepo.updateOne({
          id: data.sectionId,
          changes: () => ({ steps }),
        });
      }),
      handleMessage({
        type: requestMessage('update_program_target'),
      }),
    );
  };

  updateStepUsers = (id: TargetId, userIds: UserId[]) => {
    return ProgramTargetsAPI.updateAssignedusers(id, userIds).pipe(
      tap(data => {
        const section = this.programSectionRepo.getItem(data.sectionId)!;
        const steps = section.steps.map(step => {
          if (step.id === id) {
            return { ...step, ...data };
          }
          return step;
        });
        this.programSectionRepo.updateOne({
          id: data.sectionId,
          changes: () => ({ steps }),
        });
      }),
      handleMessage({
        type: requestMessage('update_assignee_steps'),
      }),
    );
  };
}
